import {Form, Input} from 'antd';
import React, {useState, useEffect, ReactElement} from 'react';

import {CustomSelect} from 'components/form/inputs/custom-select';
import {
    IGroupCompanyDto,
} from 'pages/ais/organization-structure-v5/api/types';
import {replaceEmptyWithNull} from 'shared/utils/arrays';

import {IGroupCompanyInfoForm, IOption} from '../../../model/types';
import {CompanyGroupStatePartInfoItem} from './company-group-state-part-info-item';
import {DependentPartListLitem} from './dependent-part-list-item';
import styles from './group-company-form.module.less';
import {GroupCompanyFormlistButtons} from './group-company-formlist-buttons';
import {GroupHoldingInfoListLitem} from './group-holding-info-list-item';

export const GroupCompanyInfoForm = <T,>({fields, onFinish, formInstance}: IGroupCompanyInfoForm<T>): ReactElement => {
    const [selectedGosRows, setSelectedGosRows] = useState<number[]>([]);

    const [isCompanyGroupStatePartInfo, setIsCompanyGroupStatePartInfo] = useState<boolean>(false);

    const [selectedGroupHoldingInfoRows, setSelectedGroupHoldingInfoRows] = useState<number[]>([]);

    const [selectedDependentPartRows, setSelectedDependentPartRows] = useState<number[]>([]);

    const onChange = (_: any, option: IOption) => {
        const isVisible = option.children === 'да';
        setIsCompanyGroupStatePartInfo(isVisible);
    };

    useEffect(() => {
        if (fields?.statePart) {
            const isVisible = fields.statePart.description === 'да';
            setIsCompanyGroupStatePartInfo(isVisible);
        }
    }, [fields?.statePart]);

    const handleFinish = (values: T) => {
        const normalizedValues = {
            ...values,
            companyGroupParticipantInfoDtos: replaceEmptyWithNull(
                (values as unknown as IGroupCompanyDto).companyGroupParticipantInfoDtos,
                ['companyGroupShareParentInfoDtos'],
            ),
            ...((values as unknown as IGroupCompanyDto).companyGroupStatePartInfoDtos !== undefined && {
                companyGroupStatePartInfoDtos: replaceEmptyWithNull(
                    (values as unknown as IGroupCompanyDto).companyGroupStatePartInfoDtos
                ),
            }),
        };
        onFinish?.(normalizedValues);
    };

    return (
        <Form
            layout="vertical"
            form={formInstance}
            onFinish={handleFinish}
        >
            <section>
                <div>
                    <h4 className={styles['group-company-form__title']}>Основные сведения</h4>
                    <div className="d-flex gap-2">
                        <Form.Item
                            name="inn"
                            label="ИНН Организации"
                            rules={[
                                {required: true},
                                {pattern: /^\d{10}$/, message: 'Количество цифр должно быть равным 10'},
                            ]}
                            validateTrigger="onBlur"
                            className={styles['group-company-form__half-width']}
                            initialValue={fields?.inn}
                        >
                            <Input
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="statePart"
                            label="Признак государственного участия"
                            rules={[{required: true}]}
                            className={styles['group-company-form__half-width']}
                            initialValue={fields?.statePart}
                        >
                            <CustomSelect
                                onChange={onChange}
                                settings={{
                                    url: 'valueLists/FLEX_VALUE?sublistCode=state_part',
                                    valuePath: 'id',
                                    labelPath: 'description',
                                }}
                            />
                        </Form.Item>
                    </div>
                </div>
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.statePart !== currentValues.statePart
                        || prevValues.companyGroupStatePartInfoDtos?.length
                            !== currentValues.companyGroupStatePartInfoDtos?.length
                    }
                >
                    {() =>
                        isCompanyGroupStatePartInfo && (
                            <>
                                <div className={styles['group-company-form__title']}>
                                    Сведения о государственном участии
                                </div>
                                <Form.List name="companyGroupStatePartInfoDtos">
                                    {(fieldsList, operations) => {
                                        if (fieldsList.length === 0) {
                                            operations.add();
                                        }

                                        return (
                                            <div>
                                                <GroupCompanyFormlistButtons
                                                    operations={operations}
                                                    fieldsList={fieldsList}
                                                    selectedRows={selectedGosRows}
                                                    setSelectedRows={setSelectedGosRows}
                                                />

                                                {fieldsList.map((field) => (
                                                    <CompanyGroupStatePartInfoItem
                                                        field={field}
                                                        key={field?.key}
                                                        form={formInstance}
                                                        selectedRows={selectedGosRows}
                                                        setSelectedRows={setSelectedGosRows}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    }}
                                </Form.List>
                            </>
                        )
                    }
                </Form.Item>
            </section>

            <section>
                <h4 className={styles['group-company-form__title']}>Сведения о группе компаний (холдинге)</h4>
                <Form.List name="companyGroupHoldingInfoDtos">
                    {(fieldsList, operations) => {
                        if (fieldsList.length === 0) {
                            operations.add();
                        }

                        return (
                            <div>
                                <GroupCompanyFormlistButtons
                                    operations={operations}
                                    fieldsList={fieldsList}
                                    selectedRows={selectedGroupHoldingInfoRows}
                                    setSelectedRows={setSelectedGroupHoldingInfoRows}
                                />

                                {fieldsList.map((field) => (
                                    <GroupHoldingInfoListLitem
                                        field={field}
                                        key={field?.key}
                                        selectedRows={selectedGroupHoldingInfoRows}
                                        setSelectedRows={setSelectedGroupHoldingInfoRows}
                                    />
                                ))}
                            </div>
                        );
                    }}
                </Form.List>
            </section>

            <section>
                <h4 className={styles['group-company-form__title']}>
                    Сведения об участниках и о дочерних (зависимых) обществах
                </h4>
                <Form.List name="companyGroupParticipantInfoDtos">
                    {(fieldsList, operations) => {
                        if (fieldsList.length === 0) {
                            operations.add();
                        }

                        return (
                            <div>
                                <GroupCompanyFormlistButtons
                                    operations={operations}
                                    fieldsList={fieldsList}
                                    selectedRows={selectedDependentPartRows}
                                    setSelectedRows={setSelectedDependentPartRows}
                                />

                                {fieldsList.map((field) => (
                                    <DependentPartListLitem
                                        field={field}
                                        key={field?.key}
                                        form={formInstance}
                                        selectedRows={selectedDependentPartRows}
                                        setSelectedRows={setSelectedDependentPartRows}
                                    />
                                ))}
                            </div>
                        );
                    }}
                </Form.List>
            </section>

            <Form.Item
                name="id"
                hidden
            />
        </Form>
    );
};
